import { Box, Tooltip, Typography } from '@mui/material';
import { alpha, useTheme, type Theme } from '@mui/material/styles';
import { motion } from 'framer-motion';
import {
  APPLICATION_DETAIL_TAB_ORDER,
  isApplicationTabEnabled,
  tabStatusLabel,
  type ApplicationTabKey,
  type ApplicationTabStatus,
} from '../../lib/applicationDetailTabStatus';
import type { ApplicationJourneyHeaderDto } from '../../api/admin';
import { TabStatusIndicator } from './TabStatusIndicator';

function statusColor(theme: Theme, status: ApplicationTabStatus): string {
  switch (status) {
    case 'pass':
      return theme.palette.success.main;
    case 'review':
      return theme.palette.warning.main;
    case 'fail':
      return theme.palette.error.main;
    case 'pending':
      return theme.palette.text.disabled;
    default:
      return theme.palette.text.secondary;
  }
}

function TabButton({
  label,
  status,
  tabKey,
  application,
  selected,
  disabled,
  onClick,
}: {
  label: string;
  status: ApplicationTabStatus;
  tabKey: ApplicationTabKey;
  application?: ApplicationJourneyHeaderDto;
  selected: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  const theme = useTheme();
  const statusMain = statusColor(theme, status);
  const hasOutcome = status === 'pass' || status === 'review' || status === 'fail';
  const chromeMain = hasOutcome ? statusMain : theme.palette.primary.main;
  const pulse = status === 'review' && !disabled;
  const statusText = tabStatusLabel(status, { tabKey, application });
  const tooltip = disabled
    ? 'Not started yet — complete earlier verification steps first'
    : status === 'fail'
      ? `${statusText} — open this tab for details`
      : statusText;

  const button = (
    <Box
      component={motion.button}
      type="button"
      disabled={disabled}
      onClick={disabled ? undefined : onClick}
      whileHover={disabled ? undefined : { scale: 1.02, y: -1 }}
      whileTap={disabled ? undefined : { scale: 0.97 }}
      animate={
        selected
          ? { scale: 1 }
          : pulse
            ? { boxShadow: [`0 0 0 0 ${alpha(statusMain, 0.4)}`, `0 0 0 6px ${alpha(statusMain, 0)}`] }
            : {}
      }
      transition={
        pulse && !selected
          ? { duration: 1.8, repeat: Infinity, ease: 'easeOut' }
          : { type: 'spring', stiffness: 420, damping: 28 }
      }
      sx={{
        position: 'relative',
        flex: 1,
        minWidth: 0,
        width: '100%',
        border: 'none',
        cursor: disabled ? 'not-allowed' : 'pointer',
        borderRadius: 2.5,
        opacity: disabled ? 0.45 : 1,
        px: { xs: 1, sm: 1.5 },
        py: 1.1,
        bgcolor: 'transparent',
        color: selected ? chromeMain : theme.palette.text.secondary,
        fontFamily: 'inherit',
        fontSize: '0.8125rem',
        fontWeight: selected ? 700 : 500,
        outline: 'none',
        zIndex: selected ? 2 : 1,
        '&:focus-visible': {
          boxShadow: `0 0 0 2px ${alpha(chromeMain, 0.5)}`,
        },
        '&:disabled': {
          cursor: 'not-allowed',
        },
      }}
    >
      {selected ? (
        <Box
          component={motion.span}
          layoutId="application-detail-tab-pill"
          transition={{ type: 'spring', stiffness: 380, damping: 32 }}
          sx={{
            position: 'absolute',
            inset: 0,
            borderRadius: 2.5,
            bgcolor: alpha(chromeMain, 0.14),
            border: `1px solid ${alpha(chromeMain, 0.4)}`,
            boxShadow: `0 4px 14px ${alpha(chromeMain, 0.22)}`,
          }}
        />
      ) : null}

      {!selected && status !== 'neutral' ? (
        <Box
          component={motion.span}
          aria-hidden
          animate={
            status === 'review'
              ? { opacity: [0.35, 0.7, 0.35] }
              : status === 'fail'
                ? { opacity: [0.5, 0.72, 0.5] }
                : { opacity: 0.5 }
          }
          transition={
            status === 'review' || status === 'fail'
              ? { duration: 2, repeat: Infinity }
              : undefined
          }
          sx={{
            position: 'absolute',
            inset: 0,
            borderRadius: 2.5,
            bgcolor: alpha(statusMain, status === 'pending' ? 0.06 : status === 'fail' ? 0.1 : 0.12),
            border: `1px solid ${alpha(statusMain, status === 'fail' ? 0.38 : 0.35)}`,
            '@media (prefers-reduced-motion: reduce)': {
              opacity: status === 'fail' ? 0.65 : 0.5,
            },
          }}
        />
      ) : null}

      <Box
        component="span"
        sx={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.65,
          width: '100%',
          flexWrap: 'wrap',
        }}
      >
        <TabStatusIndicator status={status} selected={selected} pulse={pulse} color={statusMain} />
        <Typography
          component="span"
          variant="inherit"
          sx={{
            fontWeight: 'inherit',
            lineHeight: 1.2,
            whiteSpace: { xs: 'normal', md: 'nowrap' },
            textAlign: 'center',
          }}
        >
          {label}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Tooltip title={tooltip} placement="top" arrow>
      {disabled ? (
        <Box component="span" sx={{ flex: 1, display: 'flex', minWidth: 0 }}>
          {button}
        </Box>
      ) : (
        button
      )}
    </Tooltip>
  );
}

export function ApplicationDetailTabBar({
  value,
  onChange,
  tabStatuses,
  tabCounts,
  application,
}: {
  value: number;
  onChange: (index: number) => void;
  tabStatuses: Record<ApplicationTabKey, ApplicationTabStatus>;
  application?: ApplicationJourneyHeaderDto;
  tabCounts?: Partial<Record<ApplicationTabKey, number>>;
}) {
  const theme = useTheme();

  return (
    <Box
      component={motion.div}
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
      sx={(t) => ({
        position: 'sticky',
        top: { xs: 0, md: 'var(--admin-topbar-height, 52px)' },
        zIndex: 1000,
        mb: 2,
        p: 0.75,
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: alpha(t.palette.background.paper, 0.95),
        backdropFilter: 'blur(8px)',
        boxShadow: `inset 0 1px 0 ${alpha(theme.palette.common.white, 0.06)}, 0 6px 16px ${alpha(t.palette.common.black, 0.08)}`,
        width: '100%',
      })}
    >
      <Box
        sx={{
          display: 'flex',
          gap: 0.5,
          position: 'relative',
        }}
      >
        {APPLICATION_DETAIL_TAB_ORDER.map(({ key, label }, index) => {
          const status = tabStatuses[key];
          const enabled = isApplicationTabEnabled(key, status);
          const selected = value === index;
          const count = tabCounts?.[key];
          return (
            <TabButton
              key={key}
              tabKey={key}
              application={application}
              label={count ? `${label} · ${count}` : label}
              status={status}
              selected={selected}
              disabled={!enabled && !selected}
              onClick={() => onChange(index)}
            />
          );
        })}
      </Box>
    </Box>
  );
}
